import { useMemo, useState } from "react";

/**
 * useSortableData — generic client-side table sort.
 * Click cycle: unsorted -> asc -> desc -> unsorted (original order).
 *
 * `accessors` lets a column sort by a derived value instead of a raw
 * field (e.g. sorting a "Status" column by a rank number, not the label).
 */
export function useSortableData(items, { accessors = {} } = {}) {
    const [sortKey, setSortKey] = useState(null);
    const [direction, setDirection] = useState(null); // 'asc' | 'desc' | null

    const sorted = useMemo(() => {
        const list = Array.isArray(items) ? items : [];
        if (!sortKey || !direction) return list;
        const getVal = accessors[sortKey] || ((row) => row?.[sortKey]);
        return [...list].sort((a, b) => {
            let va = getVal(a);
            let vb = getVal(b);
            if (va == null) va = "";
            if (vb == null) vb = "";
            if (typeof va === "string") va = va.toLowerCase();
            if (typeof vb === "string") vb = vb.toLowerCase();
            if (va < vb) return direction === "asc" ? -1 : 1;
            if (va > vb) return direction === "asc" ? 1 : -1;
            return 0;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        });
    }, [items, sortKey, direction]);

    function requestSort(key) {
        if (sortKey !== key) { setSortKey(key); setDirection("asc"); return; }
        if (direction === "asc") { setDirection("desc"); return; }
        setSortKey(null); setDirection(null);
    }

    return { sorted, sortKey, direction, requestSort };
}
